import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowReactive,
  watch,
} from '@vue/composition-api';
import Pagination from '@/components/pagination/pagination.vue';
import ImportRuleNode from './rule-node.vue';
import ImportGuide from './import-guide.vue';
import ImportIssues from './import-issues.vue';
import ImportProviderPayload from './provider-payload.vue';
import ImportMatrixTags from './import-matrix-tags.vue';
import LocationEditor from './location-editor.vue';
import { editorGroups, issueSection, locationPaths, localDateValue, dateCorrection, reviewStatus, attentionReason } from './correction-editor';
import { importedJobsClient as client } from './client';
import {
  correctionPayload,
  comparisonFields,
  recordCanMutate,
  createRule,
  displayValue,
  errorStatus,
  fieldValue,
  filtersFromQuery,
  providerAvailable,
  providerOptions,
  queryText,
  approvalReasons,
  recruitJobUrl,
  withUpdatedRow,
  validationMessages,
  attentionReasons,
} from './helpers';
import {
  CorrectionDraft,
  FieldDescriptor,
  ImportConnection,
  ImportedJobDetail,
  ImportedJobSummary,
  ImportFormat,
  ImportRules,
  RulePreview,
  RuleSet,
  ImportProvider,
} from './types';

export default defineComponent({
  components: { Pagination, ImportRuleNode, LocationEditor, ImportGuide, ImportIssues, ImportMatrixTags, ImportProviderPayload },
  setup(_, context) {
    const stateLabel = (value: string) =>
      context.root.$te(`pages.imported-jobs.statuses.${value}`)
        ? String(context.root.$t(`pages.imported-jobs.statuses.${value}`))
        : value;
    const t = (key: string) => String(context.root.$t(`pages.imported-jobs.${key}`));
    const detailHeading = ref<HTMLElement | null>(null);
    const detailPanel = ref<HTMLElement | null>(null);
    const rulePanel = ref<HTMLElement | null>(null);
    const state = shallowReactive({
      tab: 'imports',
      providers: [] as ImportProvider[],
      providersLoading: true,
      providersError: '',
      items: [] as ImportedJobSummary[],
      total: 0,
      allCount: null as number | null,
      attentionCount: null as number | null,
      reasonCounts: {} as Record<string, number>,
      connections: [] as ImportConnection[],
      listLoading: false,
      listError: '',
      connectionsError: '',
      detailLoading: false,
      detailError: '',
      detail: null as ImportedJobDetail | null,
      fields: [] as FieldDescriptor[],
      manualApproval: null as boolean | null,
      drafts: {} as Record<string, CorrectionDraft>,
      busy: '',
      notice: '',
      editorVersion: 0,
      locationValid: true,
      actionErrors: [] as string[],
      search: '',
      format: '',
      company: '',
      status: '',
    });
    const rule = shallowReactive({
      format: '' as ImportFormat,
      service: '',
      data: null as ImportRules | null,
      fields: [] as FieldDescriptor[],
      mode: 'custom',
      draft: { root: null } as RuleSet,
      manualApproval: false,
      loading: false,
      busy: '',
      error: '',
      messages: [] as string[],
      notice: '',
      preview: null as RulePreview | null,
      previewPage: 1,
      valid: true,
      dirty: false,
      // Live jobs the unsaved rules exclude; a second save confirms withdrawing them.
      withdrawWarning: 0,
    });
    let listSequence = 0;
    let detailSequence = 0;
    let ruleSequence = 0;
    let previewSequence = 0;
    let disposed = false;
    const filters = computed(() => filtersFromQuery(context.root.$route.query));
    const selectedId = computed(() => queryText(context.root.$route.query.id));
    const page = computed(() => filters.value.page);
    const dirty = computed(() => Object.keys(state.drafts).length > 0);
    const contentFields = computed(() => comparisonFields(state.fields));
    const groups = computed(() => editorGroups(state.fields));
    const editableFields = computed(() =>
      state.fields.filter(
        (field) =>
          field.source === 'effective' &&
          field.editable &&
          field.valueType !== 'object' &&
          field.valueType !== 'array'
      )
    );
    const companies = computed(() =>
      state.connections.filter(
        (connection, index, list) =>
          list.findIndex((item) => item.companyId === connection.companyId) === index
      )
    );
    const availableProvider = (format: string) => providerAvailable(state.providers, format);
    const providerName = (format: string) =>
      state.providers.find((provider) => provider.key === format)?.name || format;
    const filterProviders = computed(() => providerOptions(state.providers, state.format));
    const ruleProviders = computed(() => providerOptions(state.providers, rule.format));
    const detailAvailable = computed(() => !!state.detail && availableProvider(state.detail.format));
    const detailMutable = computed(() => recordCanMutate(state.detail, detailAvailable.value));
    const ruleAvailable = computed(() => availableProvider(rule.format));
    const ruleMutable = computed(() => ruleAvailable.value && rule.data?.canMutate === true);
    const ruleConnections = computed(() =>
      state.connections.filter((connection) => connection.format === rule.format)
    );
    const statuses = [
      'review-required',
      'approval-pending',
      'available',
      'withdrawn',
      'expired',
      'incomplete',
      'excluded',
      'eligible',
      'pending',
      'configuration-error',
      'processing',
      'draft',
      'published',
      'failed',
    ];
    const jobUrl = computed(() => {
      if (!state.detail || !state.detail.linkedJobAd) {
        return null;
      }

      return recruitJobUrl(state.detail.companyId, state.detail.linkedJobAd.id);
    });
    const rulePayload = () => {
      if (rule.mode === 'inherit' || (!rule.dirty && rule.data?.requiresConnectionDate)) {
        return null;
      }

      return rule.draft;
    };
    const updateQuery = (changes: Record<string, string | undefined>) => {
      const query = { ...context.root.$route.query, ...changes };
      Object.keys(query).forEach((key) => {
        if (!query[key]) {
          delete query[key];
        }
      });
      if (JSON.stringify(query) === JSON.stringify(context.root.$route.query)) {
        return;
      }
      context.root.$router.push({ query }).catch(() => {
        /* Duplicate navigation is harmless. */
      });
    };
    const syncFilters = () => {
      state.search = filters.value.search;
      state.format = filters.value.format;
      state.company = filters.value.company;
      state.status = filters.value.status;
    };
    const applyFilters = () =>
      updateQuery({
        search: state.search,
        format: state.format,
        company: state.company,
        status: state.status,
        view: filters.value.view,
        reason: filters.value.reason,
        page: '1',
      });
    // An emptied search applies at once; otherwise the next view or page change restores the old search from the URL.
    const clearSearch = (event: Event) => {
      if (!(event.target as HTMLInputElement).value && filters.value.search) {
        updateQuery({ search: '', view: filters.value.view, page: '1' });
      }
    };
    const changePage = (value: number) => updateQuery({ page: String(value), view: filters.value.view });
    const changeView = (view: 'attention' | 'all') => updateQuery({ view, reason: '', page: '1' });
    // The shell tabs switch both the workspace (imports or rules) and the list view.
    const showView = (view: 'attention' | 'all') => {
      state.tab = 'imports';
      changeView(view);
    };
    // Choosing the open compartment again shows every record that needs attention.
    const changeReason = (reason: string) =>
      updateQuery({ view: 'attention', reason: filters.value.reason === reason ? '' : reason, page: '1' });
    const select = async (id: string) => {
      if (dirty.value || state.busy) {
        return;
      }
      updateQuery({ id });
      await nextTick();
      detailHeading.value?.focus();
    };
    const discard = () => {
      state.drafts = {};
      state.locationValid = true;
      state.editorVersion++;
      state.actionErrors = [];
    };
    const loadList = async () => {
      const sequence = ++listSequence;
      state.listLoading = true;
      state.listError = '';
      try {
        const response = await client.list(filters.value);
        if (disposed || sequence !== listSequence) {
          return;
        }
        state.items = response.data;
        state.total = response.total;
        state.allCount = response.allCount;
        state.attentionCount = response.attentionCount;
        state.reasonCounts = response.reasonCounts || {};

        const lastPage = Math.max(1, Math.ceil(response.total / 25));
        if (filters.value.page > lastPage) {
          changePage(lastPage);
        }
      } catch {
        if (sequence === listSequence && !disposed) {
          state.listError = t('load-error');
        }
      } finally {
        if (sequence === listSequence && !disposed) {
          state.listLoading = false;
        }
      }
    };
    // After an action only the counts change in the background; reloading the rows would move the record out of view.
    const loadCounts = async () => {
      try {
        const response = await client.list(filters.value);
        if (disposed) return;
        state.allCount = response.allCount;
        state.attentionCount = response.attentionCount;
        state.reasonCounts = response.reasonCounts || {};
      } catch {
        // The counts refresh with the next list load.
      }
    };
    const showUpdated = (updated: ImportedJobDetail) => {
      state.items = withUpdatedRow(state.items, updated);
      loadCounts();
    };
    const loadProviders = async () => {
      state.providersLoading = true;
      state.providersError = '';
      try {
        const providers = await client.providers();
        if (disposed) {
          return;
        }
        state.providers = providers;
        if (!rule.format) {
          rule.format = state.format || providers[0]?.key || '';
        }
      } catch {
        if (!disposed) {
          state.providers = [];
          state.providersError = t('providers-error');
        }
      } finally {
        if (!disposed) {
          state.providersLoading = false;
        }
      }
    };
    const loadConnections = async () => {
      state.connectionsError = '';
      try {
        const connections = await client.connections();
        if (!disposed) {
          state.connections = connections;
        }
      } catch {
        if (!disposed) {
          state.connectionsError = t('connections-error');
        }
      }
    };
    // The connection's policy; the record's own approval state also reflects Matrix tags and held live-job updates.
    const connectionManualApproval = async (detail: ImportedJobDetail) => {
      if (!availableProvider(detail.format)) return null;
      try {
        return (await client.rules(detail.format, detail.serviceId)).manualApproval;
      } catch {
        return null;
      }
    };
    const approvalReasonKeys = computed(() =>
      state.detail ? approvalReasons(state.detail, state.manualApproval) : []
    );
    const loadDetail = async (preserveNotice = false) => {
      const sequence = ++detailSequence;
      const id = selectedId.value;
      state.detail = null;
      state.fields = [];
      state.manualApproval = null;
      discard();
      state.detailError = '';
      if (!preserveNotice) {
        state.notice = '';
      }
      if (!id) {
        state.detailLoading = false;
        return;
      }
      state.detailLoading = true;
      try {
        const detail = await client.detail(id);
        // Started together; the policy request never rejects.
        const policy = connectionManualApproval(detail);
        const fields = availableProvider(detail.format)
          ? await client.fields(detail.format, detail.serviceId, id)
          : [];
        const manualApproval = await policy;
        if (disposed || sequence !== detailSequence || selectedId.value !== id) {
          return;
        }
        state.detail = detail;
        state.fields = fields;
        state.manualApproval = manualApproval;
      } catch (error) {
        if (sequence === detailSequence && !disposed) {
          state.detailError = t(errorStatus(error) === 404 ? 'not-found' : 'load-error');
        }
      } finally {
        if (sequence === detailSequence && !disposed) {
          state.detailLoading = false;
        }
      }
    };
    const revealFocusedField = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      const panel = detailPanel.value;

      if (!panel || target.closest('.import-review-toolbar')) {
        return;
      }

      const toolbar = panel.querySelector<HTMLElement>('.import-review-toolbar');
      if (!toolbar) return;

      panel.style.setProperty('scroll-padding-top', `${toolbar.getBoundingClientRect().height + 16}px`);
      target.scrollIntoView({ block: 'nearest' });
    };
    const edit = async (field: FieldDescriptor, mode: CorrectionDraft['mode'] = 'value') => {
      if (!state.detail || !detailMutable.value || state.busy) {
        return;
      }
      const value = fieldValue(state.detail.effective, field.path);
      if (!state.drafts[field.path] || mode !== 'value') {
        state.drafts = {
          ...state.drafts,
          [field.path]: reactive<CorrectionDraft>({
            mode,
            text: field.valueType === 'date' ? localDateValue(value) : value == null ? '' : displayValue(value),
          }),
        };
      }
      await nextTick();
      const input = state.drafts[field.path].mode === 'value' ? 'value' : 'mode';
      detailPanel.value?.querySelector<HTMLElement>(`#correction-${input}-${field.path.replace(/\./g, "-")}`)?.focus();
    };
    const undo = (path: string) => {
      const drafts = { ...state.drafts };
      delete drafts[path];
      state.drafts = drafts;
    };
    const mutate = async (action: 'Corrections' | 'Reevaluate' | 'Approve' | 'Retry') => {
      const detail = state.detail;
      if (!detail || state.busy || !detailMutable.value) {
        return;
      }
      if ((action === 'Approve' && !detail.canApprove) ||
          (action === 'Retry' && !detail.canRetry) ||
          ((action === 'Approve' || action === 'Retry') && detail.requiresSourceRefresh)) {
        return;
      }
      state.busy = action;
      state.notice = '';
      state.actionErrors = [];
      const sequence = detailSequence;
      try {
        let updated: ImportedJobDetail;
        if (action === 'Corrections') {
          let payload;
          try {
            if (!state.locationValid) {
              state.actionErrors = [t('location-invalid')];
              return;
            }
            const invalidInput = detailPanel.value?.querySelector<HTMLInputElement>('input:invalid');
            if (invalidInput) {
              state.busy = '';
              const section = invalidInput.closest('details');
              if (section) section.open = true;
              await nextTick();
              invalidInput.reportValidity();
              invalidInput.focus();
              return;
            }
            const drafts = { ...state.drafts };
            state.fields.filter(field => field.source === 'effective' && field.valueType === 'date').forEach(field => {
              const draft = drafts[field.path];
              if (draft && draft.mode === 'value') {
                drafts[field.path] = { ...draft, text: dateCorrection(draft.text, fieldValue(detail.effective, field.path)) };
              }
            });
            payload = correctionPayload(drafts, state.fields);
          } catch (error) {
            state.actionErrors = [t('invalid-value') + ': ' + (error as Error).message];
            return;
          }
          updated = await client.corrections(
            detail.id,
            detail.version,
            payload.changes,
            payload.resetPaths
          );
        } else {
          updated = await client.action(action, detail.id, detail.version);
        }
        if (disposed || sequence !== detailSequence || selectedId.value !== detail.id) {
          return;
        }
        state.detail = updated;
        discard();
        state.notice = t(action === 'Corrections' ? 'corrections-saved' : 'action-done');
        showUpdated(updated);
      } catch (error) {
        if (disposed || sequence !== detailSequence || selectedId.value !== detail.id) {
          return;
        }
        if (errorStatus(error) === 409) {
          state.notice = t('conflict');
          await loadDetail(true);
          if (state.detail) showUpdated(state.detail);
        } else {
          state.actionErrors = validationMessages(error);
          if (!state.actionErrors.length) {
            state.actionErrors = [t('action-error')];
          }
        }
      } finally {
        if (!disposed) {
          state.busy = '';
        }
      }
    };
    const changeLocation = (drafts: Record<string, CorrectionDraft>) => {
      state.drafts = { ...state.drafts, ...drafts };
    };
    const undoLocation = () => {
      const drafts = { ...state.drafts };
      locationPaths.forEach((path) => {
        delete drafts[path];
      });
      state.drafts = drafts;
    };
    const resetLocation = () => {
      const drafts: Record<string, CorrectionDraft> = {};
      locationPaths.forEach((path) => {
        drafts[path] = { mode: 'reset', text: '' };
      });
      changeLocation(drafts);
    };
    const fieldLabel = (field: FieldDescriptor) => {
      const key = `pages.imported-jobs.fields.${field.path.replace(/\./g, '-')}`;
      return context.root.$te(key)
        ? String(context.root.$t(key))
        : field.label || field.path.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\./g, ' / ');
    };
    const formatDate = (value: string | null | undefined) => {
      if (!value) return '—';

      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return value;

      return date.toLocaleString(context.root.$i18n?.locale || undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    };
    const fieldDisplay = (field: FieldDescriptor, document: ImportedJobDetail['effective']) => {
      const value = fieldValue(document, field.path);
      if (value == null || (typeof value === 'string' && value.startsWith('0001-'))) return '—';
      const option = field.options?.find((item) => String(item.value) === String(value));
      if (option) return option.label;
      if (typeof value === 'boolean') return t(value ? 'yes' : 'no');
      if (field.valueType === 'date') return new Date(String(value)).toLocaleString();
      return displayValue(value);
    };
    const fieldErrors = (path: string) =>
      state.detail?.issues.filter(
        (issue) => issue.field === path || issue.field === path.split('.')[0]
      ) || [];
    const inputType = (field: FieldDescriptor) =>
      field.valueType === 'date'
        ? 'datetime-local'
        : field.valueType === 'number'
        ? 'number'
        : /Email/.test(field.path)
        ? 'email'
        : field.path === 'Application.WebAddress' || field.path === 'AdUrl'
        ? 'url'
        : 'text';
    const fixIssue = async (path: string) => {
      const section = issueSection(path);
      const element = detailPanel.value?.querySelector<HTMLElement>(`#import-section-${section}`);
      if (element instanceof HTMLDetailsElement) element.open = true;
      await nextTick();
      if (section === 'location') {
        element?.querySelector<HTMLButtonElement>('[data-edit-location]')?.click();
        await nextTick();
        element?.querySelector<HTMLElement>('.select2-selection, input')?.focus();
      } else {
        const field = state.fields.find(
          (item) =>
            item.source === 'effective' &&
            item.editable &&
            (item.path === path || item.path.startsWith(path + '.'))
        );
        if (field) await edit(field);
        else element?.focus();
      }
    };
    const installRules = (data: ImportRules) => {
      rule.data = data;
      rule.mode = data.serviceId !== null && data.customRules === null ? 'inherit' : 'custom';
      rule.draft = JSON.parse(
        JSON.stringify(
          data.serviceId === null ? data.defaultRules : data.customRules || data.effectiveRules
        )
      );
      rule.manualApproval = data.manualApproval;
      rule.preview = null;
      rule.dirty = false;
      rule.valid = true;
      rule.withdrawWarning = 0;
    };
    const loadRules = async (preserveNotice = false) => {
      const sequence = ++ruleSequence;
      ++previewSequence;
      if (!ruleAvailable.value) {
        rule.data = null;
        rule.fields = [];
        rule.preview = null;
        rule.loading = false;
        return;
      }
      const format = rule.format;
      const serviceId = rule.service ? Number(rule.service) : null;
      rule.loading = true;
      rule.error = '';
      rule.messages = [];
      rule.data = null;
      rule.preview = null;
      if (!preserveNotice) {
        rule.notice = '';
      }
      try {
        const [data, fields] = await Promise.all([
          client.rules(format, serviceId),
          client.fields(format, serviceId || undefined),
        ]);
        if (disposed || sequence !== ruleSequence) {
          return;
        }
        installRules(data);
        rule.fields = fields;
      } catch {
        if (!disposed && sequence === ruleSequence) {
          rule.error = t('load-error');
        }
      } finally {
        if (!disposed && sequence === ruleSequence) {
          rule.loading = false;
        }
      }
    };
    const changedRules = () => {
      rule.dirty = true;
      rule.preview = null;
      rule.withdrawWarning = 0;
      ++previewSequence;
    };
    const changeRuleValidity = (valid: boolean) => {
      rule.valid = valid;

      if (!valid) {
        changedRules();
      }
    };
    const updateRuleRoot = (node: RuleSet['root']) => {
      rule.draft = { root: node };
      changedRules();
    };
    const ruleMode = (mode: string) => {
      rule.mode = mode;
      rule.valid = true;
      changedRules();
    };
    const allowAll = () => {
      rule.mode = 'custom';
      rule.draft = { root: null };
      rule.valid = true;
      changedRules();
    };
    const addRoot = async () => {
      rule.mode = 'custom';
      rule.draft = { root: createRule('All') };
      rule.valid = true;
      changedRules();
      await nextTick();
      rulePanel.value?.querySelector<HTMLElement>('#import-root-kind')?.focus();
    };
    const previewRules = async (targetPage = 1) => {
      if (!rule.data || rule.busy || !rule.valid || !ruleAvailable.value) {
        return;
      }
      const sequence = ++previewSequence;
      const scopeSequence = ruleSequence;
      rule.busy = 'preview';
      rule.messages = [];
      try {
        const preview = await client.previewRules(
          rule.format,
          rule.service ? Number(rule.service) : null,
          rulePayload(),
          targetPage
        );
        if (disposed || sequence !== previewSequence || scopeSequence !== ruleSequence) {
          return;
        }
        rule.preview = preview;
        rule.previewPage = targetPage;
      } catch (error) {
        if (sequence === previewSequence && !disposed) {
          rule.messages = validationMessages(error);
          if (!rule.messages.length) {
            rule.messages = [t('action-error')];
          }
        }
      } finally {
        if (!disposed) {
          rule.busy = '';
        }
      }
    };
    const saveRules = async () => {
      if (!rule.data || rule.busy || !rule.valid || !ruleMutable.value) {
        return;
      }
      const sequence = ruleSequence;
      rule.busy = 'save';
      rule.notice = '';
      rule.messages = [];
      try {
        if (!rule.withdrawWarning) {
          const preview = await client.previewRules(
            rule.format,
            rule.service ? Number(rule.service) : null,
            rulePayload(),
            1
          );
          if (disposed || sequence !== ruleSequence) {
            return;
          }
          if (preview.liveExcluded > 0) {
            rule.preview = preview;
            rule.previewPage = 1;
            rule.withdrawWarning = preview.liveExcluded;
            return;
          }
        }
        rule.withdrawWarning = 0;
        const data = await client.saveRules(
          rule.format,
          rule.service ? Number(rule.service) : null,
          rule.data.version,
          rulePayload(),
          rule.service ? rule.manualApproval : null
        );
        if (disposed || sequence !== ruleSequence) {
          return;
        }
        installRules(data);
        if (state.detail && data.serviceId === state.detail.serviceId && data.format === state.detail.format) {
          state.manualApproval = data.manualApproval;
        }
        rule.notice = t('rules-saved');
      } catch (error) {
        if (disposed || sequence !== ruleSequence) {
          return;
        }
        if (errorStatus(error) === 409) {
          rule.notice = t('conflict');
          await loadRules(true);
        } else {
          rule.messages = validationMessages(error);
          if (!rule.messages.length) {
            rule.messages = [t('action-error')];
          }
        }
      } finally {
        if (!disposed) {
          rule.busy = '';
        }
      }
    };
    const refreshProviders = async () => {
      await loadProviders();
      await loadDetail();
      if (state.tab === 'rules') {
        await loadRules();
      }
    };
    const showRules = () => {
      state.tab = 'rules';
      if (!rule.data && !rule.loading && !rule.dirty) {
        if (ruleSequence === 0 && state.detail) {
          rule.format = state.detail.format;
        }

        loadRules();
      }
    };
    const showIssueRules = () => {
      if (!state.detail || dirty.value || state.busy) return;

      state.tab = 'rules';
      if (rule.dirty) return;

      rule.format = state.detail.format;
      rule.service = String(state.detail.serviceId);
      loadRules();
    };
    const applyChoice = async (updated: ImportedJobDetail) => {
      if (disposed || selectedId.value !== updated.id) return;

      state.detail = updated;
      state.notice = t('action-done');
      state.actionErrors = [];
      showUpdated(updated);
    };
    const staleChoice = async () => {
      state.notice = t('conflict');
      await loadDetail(true);
      if (state.detail) showUpdated(state.detail);
    };
    const clearChoice = async () => {
      const detail = state.detail;
      if (!detail || state.busy || !detailMutable.value) return;

      state.busy = 'ClearChoice';
      state.notice = '';
      state.actionErrors = [];
      const sequence = detailSequence;
      try {
        const updated = await client.clearPromotionChoice(detail.id, detail.version);
        if (sequence === detailSequence) await applyChoice(updated);
      } catch (error) {
        if (disposed || sequence !== detailSequence || selectedId.value !== detail.id) return;

        if (errorStatus(error) === 409) {
          await staleChoice();
        } else {
          state.actionErrors = validationMessages(error);
          if (!state.actionErrors.length) state.actionErrors = [t('action-error')];
        }
      } finally {
        if (!disposed) state.busy = '';
      }
    };
    const refreshRecovery = async () => {
      const id = selectedId.value;
      const sequence = detailSequence;
      try {
        const detail = await client.detail(id);
        if (disposed || sequence !== detailSequence || selectedId.value !== id) return;

        state.detail = detail;
        state.notice = t('recovery.saved');
        showUpdated(detail);
      } catch {
        if (!disposed && sequence === detailSequence) {
          state.actionErrors = [t('load-error')];
        }
      }
    };
    const changeRuleFormat = () => {
      rule.service = '';
      loadRules();
    };
    watch(
      () => JSON.stringify(filters.value),
      () => {
        syncFilters();
        loadList();
      }
    );
    watch(selectedId, () => loadDetail());
    onMounted(async () => {
      syncFilters();
      loadConnections();
      loadList();
      await loadProviders();
      loadDetail();
      if (state.tab === 'rules') {
        loadRules();
      }
    });
    onBeforeUnmount(() => {
      disposed = true;
      ++listSequence;
      ++detailSequence;
      ++ruleSequence;
      ++previewSequence;
    });
    return {
      state,
      approvalReasonKeys,
      rule,
      detailHeading,
      detailPanel,
      rulePanel,
      filters,
      selectedId,
      page,
      dirty,
      editableFields,
      contentFields,
      groups,
      reviewStatus,
      attentionReason,
      changeView,
      changeReason,
      showView,
      attentionReasons,
      revealFocusedField,
      formatDate,
      changeLocation, undoLocation, resetLocation, fieldLabel, fieldDisplay, fieldErrors, inputType, issueSection, fixIssue,
      detailMutable,
      companies,
      ruleConnections,
      statuses,
      filterProviders,
      ruleProviders,
      providerName,
      detailAvailable,
      ruleAvailable,
      ruleMutable,
      refreshProviders,
      jobUrl,
      applyFilters,
      clearSearch,
      changePage,
      select,
      discard,
      loadList,
      loadConnections,
      loadDetail,
      edit,
      undo,
      mutate,
      loadRules,
      showRules,
      showIssueRules,
      refreshRecovery,
      applyChoice,
      clearChoice,
      staleChoice,
      changeRuleFormat,
      changedRules,
      updateRuleRoot,
      changeRuleValidity,
      ruleMode,
      allowAll,
      addRoot,
      previewRules,
      saveRules,
      displayValue,
      fieldValue,
      stateLabel,
    };
  },
});
